(() => {
  if (customElements.get('blog-toolbar')) return;

  const STORAGE_KEY = 'bookcycle-blog-view';
  const SORT_VALUES = new Set(['most_recent', 'oldest', 'title_ascending', 'title_descending']);
  const VIEW_VALUES = new Set(['grid', 'list']);

  class BlogToolbar extends HTMLElement {
    connectedCallback() {
      this.handleSectionLoad = this.handleSectionLoad || ((event) => {
        if (event.target.contains(this)) this.initialize();
      });

      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      this.initialize();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    }

    initialize() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      this.articleList = this.querySelector('[data-blog-articles]');
      this.sortSelect = this.querySelector('[data-blog-sort]');
      this.viewButtons = [...this.querySelectorAll('[data-blog-view]')];

      if (!this.articleList || !this.sortSelect) return;

      this.classList.add('blog-toolbar--enhanced');

      const params = new URLSearchParams(window.location.search);
      const requestedSort = params.get('sort_by');
      const initialSort = SORT_VALUES.has(requestedSort) ? requestedSort : this.dataset.defaultSort;
      const initialView = this.readStoredView() || this.dataset.defaultView;

      this.sortSelect.value = SORT_VALUES.has(initialSort) ? initialSort : 'most_recent';
      this.setView(VIEW_VALUES.has(initialView) ? initialView : 'grid', false);
      this.sortArticles(this.sortSelect.value);
      this.updatePaginationLinks(this.sortSelect.value);
      this.restoreSearchTerm(params);

      this.sortSelect.addEventListener('change', this.onSortChange.bind(this), {
        signal: this.abortController.signal,
      });

      this.viewButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => this.setView(button.dataset.blogView, true),
          { signal: this.abortController.signal }
        );
      });
    }

    onSortChange(event) {
      const sortValue = SORT_VALUES.has(event.target.value) ? event.target.value : 'most_recent';
      const url = new URL(window.location.href);
      const hadPage = url.searchParams.has('page') && url.searchParams.get('page') !== '1';

      url.searchParams.set('sort_by', sortValue);
      url.searchParams.delete('page');

      if (hadPage) {
        window.location.assign(url.toString());
        return;
      }

      window.history.replaceState({}, '', url);
      this.sortArticles(sortValue);
      this.updatePaginationLinks(sortValue);
    }

    sortArticles(sortValue) {
      const articles = [...this.articleList.querySelectorAll('[data-article-title]')];
      const titleCollator = new Intl.Collator(document.documentElement.lang || undefined, {
        sensitivity: 'base',
        numeric: true,
      });

      articles.sort((articleA, articleB) => {
        if (sortValue === 'title_ascending' || sortValue === 'title_descending') {
          const result = titleCollator.compare(articleA.dataset.articleTitle, articleB.dataset.articleTitle);
          return sortValue === 'title_descending' ? -result : result;
        }

        const dateA = Number(articleA.dataset.articlePublished) || 0;
        const dateB = Number(articleB.dataset.articlePublished) || 0;
        return sortValue === 'oldest' ? dateA - dateB : dateB - dateA;
      });

      articles.forEach((article) => this.articleList.appendChild(article));
    }

    setView(view, persist) {
      if (!VIEW_VALUES.has(view)) return;

      this.articleList.dataset.view = view;
      this.viewButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.blogView === view));
      });

      if (!persist) return;

      try {
        window.localStorage.setItem(STORAGE_KEY, view);
      } catch (error) {
        // Storage can be unavailable in private or restricted browsing contexts.
      }
    }

    readStoredView() {
      try {
        const storedView = window.localStorage.getItem(STORAGE_KEY);
        return VIEW_VALUES.has(storedView) ? storedView : null;
      } catch (error) {
        return null;
      }
    }

    updatePaginationLinks(sortValue) {
      this.querySelectorAll('[data-blog-pagination] a[href]').forEach((link) => {
        const url = new URL(link.href, window.location.origin);
        url.searchParams.set('sort_by', sortValue);
        link.href = url.toString();
      });
    }

    restoreSearchTerm(params) {
      const searchInput = this.querySelector('.blog-toolbar__search input[name="q"]');
      if (searchInput && params.has('q')) searchInput.value = params.get('q');
    }
  }

  customElements.define('blog-toolbar', BlogToolbar);
})();
